import { ARTICLE_PICTURE_MIME_TYPES, MERGE_FIELDS, articleHeadings, t, tocEditorHtml, type RichTextConsumer } from '@bms2/shared';
import { RichTextEditor as Editor, type RichTextPicture } from '../controls/RichTextEditor';

/**
 * The Rich Text Editor as the modal system names it (piece 5; decision 0298):
 * one editor, built once in the controls, with the consumer deciding whether
 * the email additions and Insert Placeholder are offered. 💰 A second editor
 * of its own stood here until 2026-09-11, with text glyphs for a toolbar; the
 * product owner's look at the deployed one ("it is ugly") retired it.
 */
export const richTextEditorDocumentedStates = ['emailTemplate', 'knowledgeBase'] as const;

export interface RichTextEditorProps {
  consumer: RichTextConsumer;
  label: string;
  value: string;
  onChange: (html: string) => void;
  /** 🔒 The article's picture upload (decision 0800): the File's id for the words, and an address to draw it from while writing. */
  onPicture?: ((picked: File) => Promise<RichTextPicture | { problem: string }>) | undefined;
}

/**
 * 🔒 The knowledge base's Insert Table of Contents (decision 0750): the marker
 * drawn with the headings the article carries now, so the author sees what it
 * will list; the save stores it empty and every render computes it again.
 */
export const ARTICLE_INSERTS = [
  { key: 'toc', label: t('rte.toc'), icon: 'tableOfContents' as const, html: (current: string) => tocEditorHtml(articleHeadings(current), t('kb.toc.title')) },
];

/**
 * 🔨 A picture tool with nowhere to upload to is not drawn: the harness and any
 * caller that passes no upload keep the article's toolbar without it, since a
 * picture inserted inline would be dropped on save (decision 0800).
 */
export function RichTextEditor({ consumer, label, value, onChange, onPicture }: RichTextEditorProps) {
  const email = consumer === 'emailTemplate';
  // 🔒 The article consumer takes the style menu, the table of contents and the word-processor set, and its picture is a File (decisions 0750, 0800).
  const article = consumer === 'knowledgeBase';
  return (
    <Editor
      label={label}
      value={value}
      onChange={onChange}
      email={email}
      placeholders={email ? MERGE_FIELDS.map((field) => ({ key: field.key, label: field.key })) : []}
      name={consumer}
      styleMenu={article}
      wordProcessor={article}
      pictures={!article || onPicture !== undefined}
      onPicture={article ? onPicture : undefined}
      pictureTypes={article ? ARTICLE_PICTURE_MIME_TYPES : undefined}
      inserts={article ? ARTICLE_INSERTS : []}
    />
  );
}
